import {
  queryOptions,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { queryKeysDeliveryNote } from '../queryKeys';

import { fetchDlnNumberSuggestion } from './fetchDlnNumberSuggestion';
import { type DlnNumberSuggestionResponse } from './types';

/**
 * Generates the query options for the DLN number suggestion query so they can
 * be shared between the useQuery hook and prefetching.
 *
 * @param options - Additional options for the query.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_dln_number_suggestion_delivery_note_suggestions_dln_number_get
 */
export const getDeliveryNoteDlnNumberSuggestionQueryOptions = <
  TData = DlnNumberSuggestionResponse,
>(
  options?: Omit<
    UseQueryOptions<DlnNumberSuggestionResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) =>
  queryOptions({
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchDlnNumberSuggestion({ signal }),
    queryKey: queryKeysDeliveryNote.getDlnNumberSuggestion(),
    staleTime: 0,
    ...options,
  });

/**
 * React Query hook for fetching a suggested delivery-note number generated
 * from the current UTC timestamp. Each call returns a unique, collision-safe
 * DLN number ready to prefill in forms.
 *
 * @param options - Additional options for the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/DLN-Suggestions/operation/get_dln_number_suggestion_delivery_note_suggestions_dln_number_get
 */
export const useQueryDeliveryNoteDlnNumberSuggestion = <
  TData = DlnNumberSuggestionResponse,
>(
  options?: Omit<
    UseQueryOptions<DlnNumberSuggestionResponse, Error, TData>,
    'queryKey' | 'queryFn'
  >,
) => useQuery(getDeliveryNoteDlnNumberSuggestionQueryOptions<TData>(options));
